import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import { BsThreeDotsVertical, BsTrash, BsEye, BsPencil, BsChevronRight } from "react-icons/bs";

const ActionButtons = ({
  row,
  actions = [], // Only actions provided here will be shown
  className = '',
  displayMode = 'inline' // can be 'inline' or 'dropdown'
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position when dropdown opens and on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 4, // 4px gap
          left: rect.right
        });
      }
    };

    if (dropdownOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [dropdownOpen]);

  // Handle executing an action
  const handleAction = (action, event) => {
    event.preventDefault();
    event.stopPropagation(); // Stop propagation to prevent row click event
    if (typeof action.onClick === 'function') {
      action.onClick(row, event); // Pass the event to the onClick handler
    }
    // Close dropdown when an action is taken
    if (displayMode === 'dropdown') {
      setDropdownOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // If no actions are provided, don't render anything
  if (!actions.length) {
    return null;
  }

  // Display as regular buttons
  if (displayMode === 'inline') {
    return (
      <div className={`flex items-center justify-end ml-auto gap-2 ${className}`}>
        {actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant || 'primary'}
            size="sm"
            onClick={(e) => handleAction(action, e)}
            className={action.className}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        ))}
      </div>
    );
  }

  // Display as dropdown with ellipsis icon
  return (
    <div className={`relative ml-auto ${className}`}>
      <button
        ref={buttonRef}
        className="p-1.5 ml-auto rounded-full bg-gray-50 hover:bg-gray-100 transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-label="More actions"
      >
        <BsThreeDotsVertical className="w-4 h-4 text-gray-600" />
      </button>

      {dropdownOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-fit bg-white rounded-lg shadow-xl z-[9999] border border-gray-100"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translateX(-100%)'
          }}
        >
          <div className="py-1.5 px-2 border-b border-gray-200">
            <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">Actions</p>
          </div>
          <div className="py-1">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={(e) => handleAction(action, e)}
                disabled={action.disabled}
                className={`
                  w-full text-left px-3 py-2 text-sm transition-all duration-150
                  flex items-center gap-3 hover:bg-gray-50
                  ${action.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {action.variant === 'danger' ? (
                  <BsTrash className="w-4 h-4 text-red-600" />
                ) : action.variant === 'primary' ? (
                  <BsEye className="w-4 h-4 text-blue-600" />
                ) : action.variant === 'secondary' ? (
                  <BsPencil className="w-4 h-4 text-gray-600" />
                ) : (
                  <BsChevronRight className="w-4 h-4 text-gray-600" />
                )}
                <span className={`font-medium ${action.variant === 'danger' ? 'text-red-600' : 'text-gray-700'}`}>
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionButtons;
